import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { calculateProductQuantity } from '../../../Services/quoteCalculator'
import SendQuoteToCartButton from '../../../Components/SendQuoteToCart/SendQuoteToCartButton'

const SolProduct = ({ quoteData }) => {
  // Récupérer les données depuis Redux
  const selectedProducts = useSelector(state => state.selectedProducts.selectedProducts);
  const selectedProject = useSelector(state => state.selectedProject.selectedProject);
  const sectionNames = useSelector(state => state.selectedProducts.sectionNames);
  const userInputs = useSelector(state => state.selectedProducts.userInputs);
  const selectedChoices = useSelector(state => state.selectedProducts.selectedChoices);
  const surfaceArea = useSelector(state => state.selectedProducts.surfaceArea);
  const projectType = useSelector(state => state.selectedProducts.projectType) || selectedProject?.project_type || 'SOL';

  // Déterminer le type de sol sélectionné (PARQUET, VINYLE, STRATIFIE)
  const solType = useMemo(() => {
    const parquetChoice = selectedChoices.find(c => c.code === 'TYPE_PARQUET');
    const vinyleChoice = selectedChoices.find(c => c.code === 'TYPE_VINYLE');
    const stratifieChoice = selectedChoices.find(c => c.code === 'TYPE_STRATIFIE');
    return parquetChoice ? 'PARQUET' : (vinyleChoice ? 'VINYLE' : (stratifieChoice ? 'STRATIFIE' : null));
  }, [selectedChoices]);

  // Calculer la surface totale depuis les inputs
  const calculatedSurface = useMemo(() => {
    // La surface peut être dans userInputs ou surfaceArea
    let totalSurface = surfaceArea || 0;
    
    // Parcourir tous les inputs pour trouver les surfaces
    Object.entries(userInputs || {}).forEach(([sectionId, sectionInputs]) => {
      Object.entries(sectionInputs).forEach(([key, inputData]) => {
        const value = typeof inputData === 'object' && inputData?.value !== undefined 
          ? inputData.value 
          : inputData;
        const label = typeof inputData === 'object' && inputData?.label 
          ? inputData.label.toLowerCase() 
          : key.toLowerCase();
        
        // Chercher les valeurs de surface
        if (label.includes('surface') || label.includes('m²') || label.includes('m2')) {
          const numValue = parseFloat(value);
          if (!isNaN(numValue) && numValue > 0) {
            totalSurface += numValue;
          }
        }
      });
    });
    
    return totalSurface;
  }, [userInputs, surfaceArea]);

  // Déterminer le type de pose
  const poseType = useMemo(() => {
    const poseCollee = selectedChoices.find(c => c.code === 'POSE_COLLEE');
    const poseFlottante = selectedChoices.find(c => c.code === 'POSE_FLOTTANTE');
    return poseCollee ? 'COLLEE' : (poseFlottante ? 'FLOTTANTE' : null);
  }, [selectedChoices]);

  // Liste de tous les produits avec leurs codes et formules selon le type de sol
  const allProducts = useMemo(() => {
    if (!solType) return [];

    const products = [];

    // Produits communs à tous les types de sol
    products.push(
      { code: 'LAMBOURDE', name: 'Lambourde', formula: 'Surface x 2', unit: 'ML' },
      { code: 'VISERIE', name: 'Visserie', formula: 'Surface x 30', unit: 'UN' },
      { code: 'PLINTHE', name: 'Plinthe', formula: 'Périmètre (ML) ≈ 4 x √Surface', unit: 'ML' },
      { code: 'BARRE_DE_SEUIL', name: 'Barre de seuil', formula: '1 par 20 m²', unit: 'UN' },
      { code: 'PROFIL_DE_FINITION', name: 'Profil de finition', formula: 'Périmètre / 2.5 m', unit: 'UN' }
    );

    // Produits spécifiques selon le type de sol
    if (solType === 'TYPE_PARQUET') {
      products.push(
        { code: 'PARQUET', name: 'Parquet', formula: 'Surface + 7% (taux de chute)', unit: 'M²' },
        { code: 'SOUS_COUCHE', name: 'Sous-couche', formula: 'Surface (m²)', unit: 'M²' }
      );
      
      // Produits selon le type de pose pour parquet
      if (poseType === 'COLLEE') {
        products.push(
          { code: 'COLLANT', name: 'Colle', formula: 'Surface (m²)', unit: 'M²' }
        );
      }
    } else if (solType === 'TYPE_VINYLE') {
      products.push(
        { code: 'VINYLE', name: 'Vinyle', formula: 'Surface + 5% (taux de chute)', unit: 'M²' },
        { code: 'FILM_POLYETHYLENE', name: 'Film polyéthylène', formula: 'Surface (m²)', unit: 'M²' },
        { code: 'ISOLANT_ACOUSTIQUE', name: 'Isolant acoustique', formula: 'Surface (m²)', unit: 'M²' }
      );
    } else if (solType === 'TYPE_STRATIFIE') {
      products.push(
        { code: 'STRATIFIE', name: 'Stratifié', formula: 'Surface + 7% (taux de chute)', unit: 'M²' },
        { code: 'FILM_POLYETHYLENE', name: 'Film polyéthylène', formula: 'Surface (m²)', unit: 'M²' },
        { code: 'ISOLANT_ACOUSTIQUE', name: 'Isolant acoustique', formula: 'Surface (m²)', unit: 'M²' }
      );
    }

    return products;
  }, [solType, poseType]);

  // Calculer les quantités pour tous les produits
  const calculatedProducts = useMemo(() => {
    if (!solType || calculatedSurface <= 0) return [];

    return allProducts.map(product => {
      const quantity = calculateProductQuantity(
        product.code,
        projectType,
        calculatedSurface,
        userInputs || {},
        selectedChoices || []
      );
      
      return {
        ...product,
        quantity: quantity > 0 ? quantity : 0
      };
    }).filter(p => p.quantity > 0); // Filtrer les produits avec quantité 0
  }, [allProducts, solType, calculatedSurface, projectType, userInputs, selectedChoices]);

  // Préparer les items à afficher
  const itemsToDisplay = quoteData?.items || selectedProducts || [];

  // Fonction pour récupérer les détails du produit
  const getProductDetails = (item) => {
    if (item.name && item.price !== undefined) {
      return {
        name: item.name,
        image: item.image || "/assets/images/lalliard-lame-terrasse-focus_pin_du_nord_marron.png",
        price: item.price,
        quantity: item.quantity || 0,
        sku: item.sku || item.product_sku || ''
      };
    }
    
    if (item.product_name) {
      return {
        name: item.product_name,
        image: item.product_image || "/assets/images/lalliard-lame-terrasse-focus_pin_du_nord_marron.png",
        price: parseFloat(item.product_price) || 0,
        quantity: parseInt(item.quantity) || 0,
        sku: item.product_sku || item.sku || ''
      };
    }
    
    return {
      name: 'Produit inconnu',
      image: "/assets/images/lalliard-lame-terrasse-focus_pin_du_nord_marron.png",
      price: 0,
      quantity: 0,
      sku: ''
    };
  };

  // Fonction pour calculer la quantité selon les formules SOL
  const getCalculatedQuantity = (item) => {
    const pd = getProductDetails(item);
    const sku = pd.sku || item.sku || item.product_sku || '';
    const productCode = sku || item.code || pd.name || '';
    
    if (!productCode) {
      return pd.quantity || 1;
    }
    
    const calculatedQty = calculateProductQuantity(
      productCode,
      projectType,
      calculatedSurface,
      userInputs || {},
      selectedChoices || []
    );
    
    return calculatedQty;
  };

  const getSyncCartItems = () => {
    return itemsToDisplay
      .map((item) => {
        const pd = getProductDetails(item);
        const sku = pd.sku || item.sku || item.product_sku || '';
        const qty = getCalculatedQuantity(item);
        return { sku: String(sku).trim(), qty: Math.max(0, parseInt(qty, 10) || 0) };
      })
      .filter((i) => i.sku && i.qty > 0);
  };

  return (
    <div>
      <div className="page">
        <div className="container">
          <div className="row px-5">
            <div className="col-md-5 col-sm-5 col-12 px-0">
              <div className="image-terrasse" style={{ height: 380 }}>
                <img
                  src="/assets/images/Capture_terrace.jpg.png"
                  alt=""
                  className="w-100 h-100"
                />
              </div>
            </div>
            <div className="col-md-7 col-sm-7 col-12 px-0">
              {/* Affichage des calculs selon les formules du PDF */}
              {solType && calculatedSurface > 0 ? (
                <div>
                  {/* Tableau des calculs détaillés */}
                  <div className="mt-4 mb-4">
                    <h4 className="mb-3">Calculs détaillés (formule {solType})</h4>
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>Produit</th>
                            <th>Calcul</th>
                            <th>Quantité</th>
                          </tr>
                        </thead>
                        <tbody>
                          {calculatedProducts.map((product, idx) => (
                            <tr key={`calc-${idx}`}>
                              <td>{product.name}</td>
                              <td>{product.formula}</td>
                              <td>{product.quantity} {product.unit}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Affichage des produits sélectionnés si disponibles */}
                  {itemsToDisplay.length > 0 && (
                    <div className="mt-5">
                      <h4 className="mb-3">Produits sélectionnés et soumis</h4>
                      <div className="table-responsive">
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th>Produit</th>
                              <th>Prix unitaire (€)</th>
                              <th>Quantité</th>
                              <th>Total (€)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {itemsToDisplay.map((item, idx) => {
                              const pd = getProductDetails(item);
                              const computedQty = getCalculatedQuantity(item);
                              const lineTotal = (pd.price || 0) * (computedQty || 0);
                              
                              return (
                                <tr key={`summary-${idx}`}>
                                  <td>{pd.name}</td>
                                  <td>{pd.price ? pd.price.toFixed(2) : 'N/A'}</td>
                                  <td>{computedQty}</td>
                                  <td>{lineTotal.toFixed(2)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr>
                              <th colSpan={3} className="text-end">Total</th>
                              <th>
                                {(() => {
                                  const total = itemsToDisplay.reduce((sum, item) => {
                                    const pd = getProductDetails(item);
                                    const computedQty = getCalculatedQuantity(item);
                                    return sum + ((pd.price || 0) * (computedQty || 0));
                                  }, 0);
                                  return total.toFixed(2);
                                })()} €
                              </th>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-muted">
                    {!solType 
                      ? 'Veuillez sélectionner un type de sol (Parquet, Vinyle ou Stratifié) pour voir les calculs.'
                      : calculatedSurface <= 0
                      ? 'Veuillez remplir les dimensions du projet pour voir les calculs.'
                      : 'Aucun calcul disponible.'}
                  </p>
                </div>
              )}
              
              {/* Boutons devis et retour */}
              <div className="mt-4 mb-4 px-4 d-flex flex-wrap align-items-center gap-2">
                <SendQuoteToCartButton getSyncItems={getSyncCartItems} className="btn btn-primary rounded-0" />
                <Link to="/" className="btn btn-outline-secondary rounded-0">
                  <span className="text-2">Retour à l'accueil</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SolProduct

